import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import React, { useEffect } from "react";
import ShareSpacePanelAtom from "utilities/atoms/bookview/share-space-panel-atom";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import useBuildShareSpacePanel from "utilities/hooks/share-space-panel/use-build-share-space-panel";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { t } from "utilities/localization-utils";
import { useAtomValue } from "jotai";
import { useBookviewTabPanel } from "utilities/contexts/bookview-tab-panel/use-bookview-tab-panel";
import { useMobileSecondaryNavContent } from "utilities/contexts/use-mobile-secondary-nav/use-mobile-secondary-nav-content";

const BASE_CLASS_NAME = "c-share-space-panel-tab";

const ShareSpacePanelTab: React.FC = () => {
    const { toggleShareSpacePanel, isShareSpacePanelOpen } =
        useBookviewTabPanel();
    const { loadShareSpace } = useBuildShareSpacePanel();
    const { inMobileView: showMobileNav } = useBreakpointRanges();
    const { toggleShareSpaceModal } = useMobileSecondaryNavContent();

    const shareSpacePanel = useAtomValue(ShareSpacePanelAtom);

    const numberOfShareItems = shareSpacePanel.getShareItemsLength();
    const { isOnline } = useNetworkInformation();
    const isMobile = useBreakpoint(
        Breakpoints.Tablet,
        BreakpointComparer.MaxWidth
    );
    const content = t("offline-fallback-description");

    const classModifiers: string[] = [];
    if (isShareSpacePanelOpen) classModifiers.push("-panel-open");
    if (numberOfShareItems > 0) classModifiers.push("-has-items");

    useEffect(() => {
        loadShareSpace();
    }, [loadShareSpace]);

    return (
        <div className={BASE_CLASS_NAME}>
            {showMobileNav ? (
                <Button onClick={toggleShareSpaceModal}>
                    <Icon
                        size={IconSizes.Medium}
                        type={Icons.Share}
                        cssClassName={`${BASE_CLASS_NAME}__icon`}
                    />
                    <span className={`${BASE_CLASS_NAME}__text`}>
                        {t("sharing")} ({numberOfShareItems})
                    </span>
                </Button>
            ) : (
                <Tooltip
                    content={content}
                    cssClassName={`${BASE_CLASS_NAME}__tooltip`}
                    disabled={isMobile || isOnline}
                    durationInMs={0}
                    hideOnClick={true}
                    placement={TooltipPlacement.Bottom}>
                    <Button
                        cssClassName={`${BASE_CLASS_NAME}__button ${classModifiers.join(" ")}`}
                        size={ButtonSizes.Small}
                        style={ButtonStyles.TertiaryAlt}
                        onClick={toggleShareSpacePanel}>
                        <span className={`${BASE_CLASS_NAME}__counter`}>
                            {numberOfShareItems}
                        </span>
                        <Icon type={Icons.Share}></Icon>
                    </Button>
                </Tooltip>
            )}
        </div>
    );
};

export default ShareSpacePanelTab;
